// ============================================================================
// AUDIO MANAGER - Sound Effects and Music
// ============================================================================

export interface SoundOptions {
  volume?: number;
  loop?: boolean;
  pitch?: number;
  offset?: number; // Start position in seconds
}

export class AudioManager {
  private context: AudioContext | null = null;
  private masterVolume = 1;
  private sfxVolume = 1;
  private musicVolume = 0.5;
  
  private sounds = new Map<string, AudioBuffer>();
  private activeSources = new Map<string, AudioBufferSourceNode>();
  private activeGains = new Map<string, GainNode>(); // Track gain nodes for volume control
  private currentMusic: AudioBufferSourceNode | null = null;
  private currentMusicKey: string | null = null;

  private initialized = false;

  async init(): Promise<void> {
    if (this.initialized) return;

    try {
      this.context = new AudioContext();
      this.initialized = true;
    } catch (e) {
      console.warn('Web Audio API not supported:', e);
    }
  }

  async loadSound(key: string, url: string): Promise<void> {
    if (!this.context) return;

    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.context.decodeAudioData(arrayBuffer);
      this.sounds.set(key, audioBuffer);
    } catch (e) {
      console.warn(`Failed to load sound: ${key}`, e);
    }
  }

  play(key: string, options: SoundOptions = {}): string | null {
    if (!this.context || !this.sounds.has(key)) return null;

    const buffer = this.sounds.get(key)!;
    const source = this.context.createBufferSource();
    source.buffer = buffer;

    // Create gain node for volume
    const gainNode = this.context.createGain();
    gainNode.gain.value = (options.volume ?? 1) * this.sfxVolume * this.masterVolume;

    // Connect nodes
    source.connect(gainNode);
    gainNode.connect(this.context.destination);

    // Set options
    source.loop = options.loop ?? false;
    if (options.pitch) {
      source.playbackRate.value = options.pitch;
    }

    // Generate unique ID
    const id = `${key}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.activeSources.set(id, source);
    this.activeGains.set(id, gainNode);

    // Clean up when finished
    source.onended = () => {
      this.activeSources.delete(id);
      this.activeGains.delete(id);
    };

    // Start at offset if specified
    const offset = options.offset ?? 0;
    source.start(0, offset);
    return id;
  }

  stop(id: string): void {
    const source = this.activeSources.get(id);
    if (source) {
      source.stop();
      this.activeSources.delete(id);
      this.activeGains.delete(id);
    }
  }
  
  // Get duration of a loaded sound
  getSoundDuration(key: string): number {
    const buffer = this.sounds.get(key);
    return buffer ? buffer.duration : 0;
  }

  playMusic(key: string, fadeIn = 0): void {
    if (!this.context || !this.sounds.has(key)) return;
    if (this.currentMusicKey === key) return;

    // Stop current music
    this.stopMusic(0.5);

    const buffer = this.sounds.get(key)!;
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    const gainNode = this.context.createGain();
    
    if (fadeIn > 0) {
      gainNode.gain.value = 0;
      gainNode.gain.linearRampToValueAtTime(
        this.musicVolume * this.masterVolume,
        this.context.currentTime + fadeIn
      );
    } else {
      gainNode.gain.value = this.musicVolume * this.masterVolume;
    }

    source.connect(gainNode);
    gainNode.connect(this.context.destination);

    source.start(0);
    this.currentMusic = source;
    this.currentMusicKey = key;
  }

  stopMusic(fadeOut = 0): void {
    if (!this.currentMusic || !this.context) return;

    const source = this.currentMusic;
    
    if (fadeOut > 0) {
      const gainNode = this.context.createGain();
      source.disconnect();
      source.connect(gainNode);
      gainNode.connect(this.context.destination);
      gainNode.gain.linearRampToValueAtTime(0, this.context.currentTime + fadeOut);
      
      setTimeout(() => {
        try {
          source.stop();
        } catch {
          // Already stopped
        }
      }, fadeOut * 1000);
    } else {
      source.stop();
    }

    this.currentMusic = null;
    this.currentMusicKey = null;
  }

  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
  }

  setSFXVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
  }

  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));
  }

  // Resume audio context (needed after user interaction)
  async resume(): Promise<void> {
    if (this.context?.state === 'suspended') {
      await this.context.resume();
    }
  }

  suspend(): void {
    this.context?.suspend();
  }

  stopAll(): void {
    for (const [id] of this.activeSources) {
      this.stop(id);
    }
    this.stopMusic();
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
